'use client';

import * as Sentry from '@sentry/nextjs';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { type APIResponse, type CongestionInfo, type CongestionLevel, CongestionModal, CongestionModalType, type VariantType, utilsStyles } from '../../../..';
import iconCoinPrimary from '../../../../assets/images/icons/coin-primary.png';
import iconCoinSmall from '../../../../assets/images/icons/coin-small.png';
import ActionButton from '../../../../components/form/ActionButton/ActionButton';
import FormSelect from '../../../../components/form/FormSelect/FormSelect';
import Accordion from '../../../../components/ui/Accordion/Accordion';
import IconTextUnit from '../../../../components/ui/IconTextUnit/IconTextUnit';
import type { CalcCoinResult, CongestionModalTypeType, MultiDrawItem } from '../../types';
import styles from './LotteryCoinForm.module.scss';

const CONGESTION_LEVEL_DEFAULT = 'high'; // 混雑APIのlevelがない場合のデフォルト値
const CONGESTION_RETRY_AFTER_DEFAULT = 30; // 混雑APIのretryAfterがない場合のデフォルト値（秒）

export type LotteryCoinFormProps<T = unknown> = {
  lotteryEventId: number;
  drawFee: number;
  coinMenuItem: T;
  calcResult: CalcCoinResult;
  multiDraw: Array<MultiDrawItem>;
  storageKey: string;
  showPaymentWarning?: boolean;
  userCoin?: number;
  sessionStatus: 'loading' | 'authenticated' | 'unauthenticated';
  // 処理系の関数
  onCalcCoin: (params: { lotteryEventId: number; drawCount: number }) => Promise<{ result: CalcCoinResult }>;
  onPurchase: (coinMenuItem: T, missingCoin: number) => Promise<void>;
  onInvalid: () => void;
  onError: (messages: string[]) => Promise<void>;
  formatCurrency: (amount: number) => string;
  onGetCongestion: (params: { type: CongestionModalTypeType; lotteryEventId?: number; debugLevel?: CongestionLevel }) => Promise<void>;
  variant?: VariantType;
};

function LotteryCoinForm<T>({
  lotteryEventId,
  drawFee,
  coinMenuItem,
  calcResult,
  multiDraw,
  storageKey,
  showPaymentWarning = false,
  userCoin = 0,
  sessionStatus,
  onCalcCoin,
  onPurchase,
  onInvalid,
  onError,
  formatCurrency,
  onGetCongestion,
  variant = 'default',
}: LotteryCoinFormProps<T>) {
  const initCalcCoinResult: CalcCoinResult = calcResult;
  const searchParams = useSearchParams();

    // ?debug_congestion=high などで混雑度を指定可能(fannychanceでも同様のパラメータを利用しているため、両方の環境で統一して利用できるようにする)
  const debugCongestionLevel = searchParams.get('debug_congestion') as CongestionLevel | null;
  const [calcCoinResult, setCalcCoinResult] = useState(initCalcCoinResult);
  const [missingCoin, setMissingCoin] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpened, setIsOpened] = useState(false);

  // 混雑度チェック用state
  const [showCongestionModal, setShowCongestionModal] = useState(false);
  const [congestionInfo, setCongestionInfo] = useState<CongestionInfo | null>(null);
  const [pendingAction, setPendingAction] = useState<CongestionModalTypeType | null>(null);

  const { handleSubmit, getValues, control } = useForm({
    mode: 'onChange',
    defaultValues: {
      count: 1,
    },
  });

  const countOptions = [...Array(100).keys()].map((v) => {
    return {
      value: v + 1,
      text: (v + 1).toString(),
    };
  });

  // ===============================================================================
  // コイン購入処理
  // 不足分のコインを購入するボタン押下時、もしくは、混雑モーダルリトライ成功時に呼ばれる関数
  // ===============================================================================
  const purchase = async () => {
    try {
      setIsLoading(true);
      await setMaxDrawableMenuId(lotteryEventId);
      await onPurchase(coinMenuItem, missingCoin);
      setIsLoading(false);
    } catch (_error) {
      setIsLoading(false);
    }
  };

  const submitError = () => {
    onInvalid();
  };

  //コイン計算
  const calcCoin = useCallback(async () => {
    try {
      const { result } = await onCalcCoin({
        lotteryEventId: lotteryEventId,
        drawCount: getValues('count'),
      });

      setCalcCoinResult(result);

      //不足分を計算
      const totalDrawCoin = result.drawCoin + result.shippingCoin;
      const calcMissingCoin = totalDrawCoin - userCoin;
      setMissingCoin(calcMissingCoin < 0 ? 0 : calcMissingCoin);
    } catch (_error) {
      await onError(['エラーが発生しました']);
    }
  }, [getValues, lotteryEventId, userCoin, onCalcCoin, onError]);

  // 初回表示時にコイン不足時に不足分のコインを購入するボタンが動作するようにする
  useEffect(() => {
    // calcCoinが複数回実行されないようにセッションが認証されてuserCoinが更新された後に実行
    if (sessionStatus === 'authenticated') {
      calcCoin();
    }
  }, [calcCoin, sessionStatus]);

  // 購入後のコインで引ける最大回数のメニューIDをストレージに保存
  const setMaxDrawableMenuId = async (lotteryEventId: number) => {
    try {
      const afterPurchasedTotal = calcCoinResult.drawCoin + calcCoinResult.shippingCoin;
      // 購入コイン数＋現在コイン数で引ける最大のメニューを取得
      for (const item of multiDraw.filter((item) => item.count * drawFee <= afterPurchasedTotal)) {
        const { result } = await onCalcCoin({
          lotteryEventId: lotteryEventId,
          drawCount: item.count,
        });

        if (afterPurchasedTotal < result.drawCoin + result.shippingCoin) {
          continue;
        }
        localStorage.setItem(storageKey, item.id.toString());
        break;
      }
    } catch (_error) {
      await onError(['エラーが発生しました']);
    }
  };

  // ========================================
  // 混雑度チェック処理
  // ========================================
  // くじ引き/コイン購入の前に混雑度チェックAPI（GET /congestion）を呼び出す。
  // - type: 'lottery'（くじ引き処理）/ 'coin'（コイン購入処理）
  // - 200 OK（low/moderate）: 本処理へ進む
  // - 429 Too Many Requests（high/full）: 待機モーダルを表示し、retryAfter秒後にリトライ可能
  const checkCongestion = async (type: CongestionModalTypeType, onSuccess: () => Promise<void>) => {
    try {
      setIsLoading(true);

      // 混雑度チェックAPI実行
      await onGetCongestion({
        type,
        lotteryEventId: type === CongestionModalType.lottery.value ? lotteryEventId : undefined,
        debugLevel: debugCongestionLevel || undefined,
      });

      // 200 OK（low/moderateの場合は本処理が呼ばれます） - 既存処理を実行
      setIsLoading(false);
      await onSuccess();

    } catch (error) {
      // 200以外のレスポンスが発生した場合
      // APIResponseの形で整形する(APIResponseの型ではない場合はapiErrorにはundefinedが入る)
      const apiError = error as APIResponse<{ congestion: CongestionInfo }>;
      if (apiError?.code === 429) {
        // 混雑時 - 待機モーダル表示
        setCongestionInfo(apiError?.result?.congestion || { level: CONGESTION_LEVEL_DEFAULT, retryAfter: CONGESTION_RETRY_AFTER_DEFAULT });
        setPendingAction(type);
        setShowCongestionModal(true);
        setIsLoading(false);
        return;
      }

      // その他エラーの場合は想定外のエラーになるためSentryに送信する
      Sentry.captureException(error);

      // その他エラーの場合はコールバック処理を続行
      setIsLoading(false);
      await onSuccess();
    }
  };

  // 購入ボタン押下時（混雑度チェック → 既存処理）
  const handlePurchase = () => {
    checkCongestion(CongestionModalType.coin.value, purchase);
  };

  // 混雑モーダルでリトライ押下時
  const handleRetry = () => {
    setShowCongestionModal(false);
    handlePurchase();
  };

  // 混雑モーダルでキャンセル押下時
  const handleCancelCongestion = () => {
    setShowCongestionModal(false);
    setPendingAction(null);
  };

  return (
    <>
      <div className={utilsStyles.mtMedium}>
        <div className={`${utilsStyles.flexRowNowrap} ${utilsStyles.alignCenter} ${utilsStyles.spaceBetween}`}>
          <div>
            <p className={`${utilsStyles.textMedium} ${utilsStyles.textBold}`}>保有しているコイン</p>
            <div className={utilsStyles.textLarge}>
              <IconTextUnit text={formatCurrency(userCoin)} imgSrc={iconCoinSmall.src} />
            </div>
          </div>
          <ActionButton color='primary' size='small' onClick={() => setIsOpened(!isOpened)}>
            回数を指定して購入
          </ActionButton>
        </div>
      </div>

      <Accordion isOpened={isOpened} startHeight={0}>
        <div className={utilsStyles.mtSmall}>
          <div className={`${utilsStyles.centered} ${utilsStyles.spaceBetween}`}>
            <h1 className={utilsStyles.pageTitle}>くじ引き回数分のコインを購入</h1>
          </div>
        </div>
        <div className={utilsStyles.mtSmall}>
          <div className={styles.lotteryCoinForm}>
            <div className={styles.formHeadWrapper}>
              {/* くじ引きコイン数 */}
              <div className={utilsStyles.centered}>
                <div className={`${utilsStyles.textLarge} ${utilsStyles.textBold}`}>単価:</div>
                <div className={`${styles.formTextSize} ${utilsStyles.textBold} ${utilsStyles.mLeftXSmall}`}>
                  <IconTextUnit text={formatCurrency(drawFee)} imgSrc={iconCoinSmall.src} />
                </div>
              </div>
              {/* 回数選択 */}
              <div className={styles.formSelectWrapper}>
                <div className={`${utilsStyles.textBold} ${styles.formTextSize}`}>くじ引き回数:</div>
                <div className={`${styles.formSelectItem} ${utilsStyles.mLeftXSmall}`}>
                  <FormSelect
                    label=''
                    name='count'
                    items={countOptions}
                    control={control}
                    required
                    outlined
                    onChange={() => calcCoin()}
                  />
                </div>
              </div>
            </div>
            <div className={utilsStyles.mtSmall}>
              <hr className={utilsStyles.pinkishgrayBgColor} />
            </div>
            {/* コイン詳細 */}
            <div className={`${styles.formDetail} ${utilsStyles.mtSmall}`}>
              <div className={styles.formDetailRow}>
                <p className={styles.formTextSize}>小計</p>
                <div className={`${utilsStyles.textLarge} ${utilsStyles.textBold}`}>
                  <IconTextUnit text={formatCurrency(calcCoinResult.drawCoin)} imgSrc={iconCoinSmall.src} />
                </div>
              </div>
              <div className={styles.formDetailRow}>
                <p className={styles.formTextSize}>配送手数料</p>
                <div className={`${utilsStyles.textLarge} ${utilsStyles.textBold}`}>
                  <IconTextUnit
                    text={formatCurrency(calcCoinResult.shippingCoin)}
                    imgSrc={iconCoinSmall.src}
                  />
                </div>
              </div>
              <div className={styles.formDetailRow}>
                <hr className={utilsStyles.pinkishgrayBgColor} />
              </div>
              <div className={styles.formDetailRow}>
                <p className={styles.formTextSize}>合計</p>
                <div className={`${utilsStyles.textLarge} ${utilsStyles.textBold}`}>
                  <IconTextUnit
                    text={formatCurrency(calcCoinResult.drawCoin + calcCoinResult.shippingCoin)}
                    imgSrc={iconCoinSmall.src}
                  />
                </div>
              </div>
              <div
                className={`${styles.formDetailRow} ${utilsStyles.primaryColor}`}
                style={{ opacity: missingCoin > 0 ? 1 : 0.3 }}
              >
                <p className={styles.formTextSize}>不足分</p>
                <div className={`${utilsStyles.textLarge} ${utilsStyles.textBold}`}>
                  <IconTextUnit text={formatCurrency(missingCoin)} imgSrc={iconCoinPrimary.src} />
                </div>
              </div>
            </div>
            <div className={styles.buttonWrapper}>
              <ActionButton
                onClick={handleSubmit(handlePurchase, submitError)}
                disabled={missingCoin === 0 || isLoading}
                size='fullWidth'
                color='primary'
              >
                不足分のコインを購入する
              </ActionButton>
            </div>
            {showPaymentWarning && (
              <div
                className={`${utilsStyles.mtSmall} ${utilsStyles.textSmall} ${utilsStyles.textBold} ${utilsStyles.primaryColor}`}
              >
                <p>SNSアプリ内ブラウザやシークレットモードをご利用の場合、決済に失敗する可能性がございます。</p>
                <br />
                <p>決済エラーを防ぐには：</p>
                <ul>
                  <li>
                    ・SNSアプリ内ではなく、SafariやChromeなどのブラウザアプリから直接サイトにアクセスしてください。
                  </li>
                  <li>・ブラウザのシークレットモードやプライベートモードの使用を避けてください。</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </Accordion>

      {/* 混雑時待機モーダル */}
      <CongestionModal
        isOpen={showCongestionModal}
        title={pendingAction === CongestionModalType.coin.value ? CongestionModalType.coin.title : CongestionModalType.lottery.title}
        congestionLevel={congestionInfo?.level || CONGESTION_LEVEL_DEFAULT}
        retryAfter={congestionInfo?.retryAfter || CONGESTION_RETRY_AFTER_DEFAULT}
        onRetry={handleRetry}
        onCancel={handleCancelCongestion}
        eventEndAt={undefined}        // くじ終了日に関する説明文言は非表示にしたいのでundefinedを渡す
        isCoinPurchase={false}        // お支払い画面の準備をしている旨の説明文言は非表示にしたいのでfalseを渡す
        isFirstComeFirstServed={true} // 先着順の旨のメッセージは常に表示したいのでtrueを渡す
        variant={variant}
      />
    </>
  );
}

export default LotteryCoinForm;
